import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isNumber } from '@hub-mono/utils';
import { calculateEndDate, calculateResourceCapacity, makeSelectHolidaysByCalendarIds } from '@hub-mono/app-modules';
import { change } from 'redux-form';
import { useDispatch, useSelector } from 'react-redux';
import { makeResourceByIdFromMap } from '../../../selectors/resource';
import { selectWeekDaysSetting } from '../../../selectors/company';
import { usePrevious } from '@hub-mono/ui';
import { debounce } from 'lodash';

const MAX_NUMBER_BOOKING_DAYS = 1000;

export const useDayCounterLogic = ({ formName, fieldName, initialized, startDate, endDate, resourceId } = {}) => {
    const weekDaysSettings = useSelector(selectWeekDaysSetting);
    const dispatch = useDispatch();
    const [daysCounter, setDaysCounter] = useState(0);
    const initialDayCounterSet = useRef(false);

    const resourceSelector = useMemo(() => {
        return makeResourceByIdFromMap(resourceId);
    }, [resourceId]);

    const resource = useSelector(resourceSelector);
    const resourceHolidaysSelector = useMemo(() => makeSelectHolidaysByCalendarIds(resource?.calendarIds), [
        resource?.calendarIds,
    ]);
    const resourceHolidays = useSelector(resourceHolidaysSelector);

    useEffect(() => {
        if (!initialized || initialDayCounterSet.current) {
            return;
        }

        const { workDaysCount } = calculateResourceCapacity({
            endDate: endDate,
            startDate: startDate,
            resource: resource,
            companyWorkWeekDetails: weekDaysSettings,
            holidays: resourceHolidays,
        });


        initialDayCounterSet.current = true;
        setDaysCounter(workDaysCount);
    }, [endDate, initialized, resource, resourceHolidays, startDate, weekDaysSettings]);

    const debouncedChangeHandler = useMemo(() => {
        return debounce(newValue => {
            let newDaysCounterValue = newValue > MAX_NUMBER_BOOKING_DAYS ? MAX_NUMBER_BOOKING_DAYS : newValue;

            if (newDaysCounterValue < 0) {
                newDaysCounterValue = 0;
            }

            const { endDate: newEndDate, numberOfWorkDays } = calculateEndDate({
                startDate: startDate,
                noOfDays: newDaysCounterValue,
                resource: resource,
                companyWorkWeekDetails: weekDaysSettings,
                holidays: resourceHolidays,
            });

            setDaysCounter(numberOfWorkDays);
            dispatch(change(formName, fieldName || 'end', newEndDate));
        }, 300);
    }, [dispatch, fieldName, formName, resource, resourceHolidays, startDate, weekDaysSettings]);


    const handleDaysCounterChange = useCallback(
        event => {
            setDaysCounter(() => {
                const parsedValue = Number.parseFloat(event.target.value);

                if (!isNumber(parsedValue)) {
                    return;
                }

                debouncedChangeHandler(parsedValue);
                return parsedValue;
            });
        },
        [debouncedChangeHandler]
    );

    const prevDaysCounterValue = usePrevious(daysCounter);
    const handleDaysCounterBlur = useCallback(() => {
        setDaysCounter(daysCounterValue => {
            if (!isNumber(daysCounterValue)) {
                return prevDaysCounterValue;
            }

            return daysCounterValue;
        });
    }, [prevDaysCounterValue]);

    const handleStartDateChange = useCallback(
        start => {
            if (!endDate) {
                return;
            }

            const { workDaysCount } = calculateResourceCapacity({
                endDate: endDate,
                startDate: start,
                resource: resource,
                companyWorkWeekDetails: weekDaysSettings,
                holidays: resourceHolidays,
            });

            setDaysCounter(workDaysCount);
        },
        [endDate, resource, resourceHolidays, weekDaysSettings]
    );

    const handleEndDateChange = useCallback(
        end => {
            if (!startDate) {
                return;
            }

            const { workDaysCount } = calculateResourceCapacity({
                endDate: end,
                startDate: startDate,
                resource: resource,
                companyWorkWeekDetails: weekDaysSettings,
                holidays: resourceHolidays,
            });

            setDaysCounter(workDaysCount);
        },
        [startDate, resource, weekDaysSettings, resourceHolidays]
    );

    return {
        handleDaysCounterChange,
        daysCounter,
        handleStartDateChange,
        handleEndDateChange,
        handleDaysCounterBlur,
    };
};
